import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpService } from '../http/http.service';
import { Alert, AlertStatus, BulkUpdateAlerts, ClientAlertSummary } from '../../models/alert';
import { ApiResponse } from '../../models/api-responce';
import { UtilityService } from '../utility/utility.service';
import { AuthService } from '../../shared/services/auth-service/auth.service';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class AlertsService {
  constructor(
    private readonly _httpService: HttpService,
    private readonly _utilityService: UtilityService,
    private readonly _authService: AuthService
  ) {}

  getAlerts(filters?: any): Observable<ApiResponse<Alert>> {
    let url = '/v1/integration-alerts';
    const queryParams: Record<string, any> = {
      Size: 500,
      ...filters
    };
    url += this._utilityService.buildQueryParams(queryParams);

    const request = this._httpService.createHttpClient(this._utilityService.getApiUrl('alert')).url(url);
    return request.get<ApiResponse<Alert>>();
  }

  getAlertById(alertId: string): Observable<Alert> {
    const url = `/v1/integration-alerts/${alertId}`;
    const request = this._httpService.createHttpClient(this._utilityService.getApiUrl('alert')).url(url);
    return request.get<Alert>();
  }

  assignAlert(alertId: string): Observable<any> {
    const url = `/v1/integration-alerts/${alertId}`;
    const payload = { AssignedTo: this._authService.getEmail() };
    const request = this._httpService.createHttpClient(this._utilityService.getApiUrl('alert')).url(url);
    return request.requestBody(payload).patch();
  }

  assignAlertToUser(alertId: string, userEmail: string): Observable<any> {
    const url = `/v1/integration-alerts/${alertId}`;
    const payload = { AssignedTo: userEmail };
    const request = this._httpService.createHttpClient(this._utilityService.getApiUrl('alert')).url(url);
    return request.requestBody(payload).patch();
  }

  updateAlertStatus(alertId: string, status: AlertStatus): Observable<any> {
    const url = `/v1/integration-alerts/${alertId}`;
    const payload = { Status: status };
    const request = this._httpService.createHttpClient(this._utilityService.getApiUrl('alert')).url(url);
    return request.requestBody(payload).patch();
  }

  bulkUpdateAlerts(payload: BulkUpdateAlerts): Observable<any> {
    const url = '/v1/integration-alerts/bulk-update';
    const request = this._httpService.createHttpClient(this._utilityService.getApiUrl('alert')).url(url);
    return request.requestBody(payload).patch();
  }

  getOpenAlertsCount(clientId: string, environmentId: string): Observable<number> {
    return this.getClientOpenAlertsSummary(clientId).pipe(map(summary => summary?.Environments?.find(e => e.EnvironmentId === environmentId)?.AlertsCount ?? 0));
  }

  getAllClientsOpenAlertsSummary(): Observable<ClientAlertSummary[]> {
    const url = '/v1/integration-alerts/summary?Status=OPEN';
    const request = this._httpService.createHttpClient(this._utilityService.getApiUrl('alert')).url(url);

    return request.get<ClientAlertSummary[]>();
  }

  /**
   * Server-aggregated open-alert summary for a single client (env totals + per-criticality and
   * per-type breakdowns). Unlike getAlerts(), the counts are exact regardless of alert volume.
   */
  getClientOpenAlertsSummary(clientId: string): Observable<ClientAlertSummary | undefined> {
    const url = `/v1/integration-alerts/summary?Status=OPEN&ClientId=${clientId}`;
    const request = this._httpService.createHttpClient(this._utilityService.getApiUrl('alert')).url(url);

    return request.get<ClientAlertSummary[]>().pipe(map(list => (list ?? []).find(c => c.ClientId === clientId)));
  }
}
